
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { createUserTechnique, createOfficialTechnique } from '@/services/index';
import { toast } from '@/hooks/use-toast';
import {
  createTechniqueSchema,
  defaultFormValues,
  CreateTechniqueFormValues,
  BasicInfoFields,
  TechniqueTypeFields,
  PostureField,
  DescriptionField,
  MediaUrlFields,
  KeyPointsField,
  CommonMistakesField,
  PersonalNotesField,
  FormActions
} from './create-technique-form';

interface CreateTechniqueFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  isAdminCreating?: boolean;
}

export function CreateTechniqueForm({ 
  onSuccess, 
  onCancel, 
  isAdminCreating = false 
}: CreateTechniqueFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set default form values based on whether an admin is creating
  const customDefaultValues = {
    ...defaultFormValues,
    isPrivate: isAdminCreating ? false : true, // Admin created techniques are public by default
  };

  const form = useForm<CreateTechniqueFormValues>({
    resolver: zodResolver(createTechniqueSchema),
    defaultValues: customDefaultValues,
  });

  // Handle form submission
  const onSubmit = async (values: CreateTechniqueFormValues) => {
    setIsSubmitting(true);
    try {
      // Convert comma-separated strings to arrays before submitting
      const keyPointsArray = values.keyPoints
        .split(',')
        .map((point: string) => point.trim())
        .filter(Boolean);
        
      const commonMistakesArray = values.commonMistakes
        .split(',')
        .map((mistake: string) => mistake.trim())
        .filter(Boolean);
      

      
      const techniqueData = {
        name: values.name,
        description: values.description,
        techniquePosition: values.techniquePosition,
        techniqueType: values.techniqueType,
        difficulty: values.difficulty,
        positionId: '', // We're removing position selection, so this is empty
        imageUrl: values.imageUrl || undefined,
        videoUrl: values.videoUrl || undefined,
        category: values.posture, // Map posture to category field
        subcategory: '', // Remove subcategory
        keyPoints: keyPointsArray,
        commonMistakes: commonMistakesArray,
        personalNotes: values.personalNotes || undefined,
        isPrivate: isAdminCreating ? false : values.isPrivate,
      };
      
      // Choose the appropriate creation function based on who's creating
      if (isAdminCreating) {
        await createOfficialTechnique(techniqueData);
        toast({
          title: "Success",
          description: "Official technique created successfully and added to the library",
        });
      } else {
        await createUserTechnique(techniqueData);
        toast({
          title: "Success",
          description: values.isPrivate 
            ? "Private technique created successfully" 
            : "Public technique created successfully and added to the library",
        });
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred while creating the technique",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info (Name & Privacy) */}
        <BasicInfoFields form={form} isAdminCreating={isAdminCreating} />

        {/* Technique Type Fields */}
        <TechniqueTypeFields form={form} />

        {/* Posture Field */}
        <PostureField form={form} />

        {/* Description Field */}
        <DescriptionField form={form} />

        {/* Media URL Fields */}
        <MediaUrlFields form={form} />

        {/* Key Points Field */}
        <KeyPointsField form={form} />

        {/* Common Mistakes Field */}
        <CommonMistakesField form={form} />

        {/* Personal Notes Field - hide for admin creation */}
        {!isAdminCreating && <PersonalNotesField form={form} />}

        {/* Form Actions */}
        <FormActions 
          isSubmitting={isSubmitting} 
          onCancel={onCancel} 
          submitLabel={isAdminCreating ? "Create Official Technique" : "Create Technique"}
        />
      </form>
    </Form>
  );
}
